import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Tag, X, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Recovery Discount Banner.
 *
 * Polls /api/payments/recovery/active-offer once per dashboard render.
 * If the user has an abandoned cart inside the recovery window, shows a
 * compact "50% off — finish your order" banner with a Resume CTA that
 * routes to /cart-recovery (which then routes to the right checkout
 * with the discount auto-applied SERVER-SIDE — no coupon code needed).
 *
 * Banner auto-disappears once the webhook flips `recovery_redeemed=true`
 * (i.e., the user pays at the discounted amount).
 *
 * Drops cleanly above any dashboard; if there's no active offer, renders
 * nothing.
 */
const RecoveryDiscountBanner = ({ dataTestId = 'recovery-discount-banner' }) => {
  const [offer, setOffer] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/payments/recovery/active-offer`);
        if (!cancelled) setOffer(r.data?.offer || null);
      } catch { /* unauthed or no offer — silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!offer || !offer.active || dismissed) return null;

  const sym = { GBP: '£', INR: '₹', CAD: 'C$', USD: '$' }[offer.currency] || '';
  const fmt = (n) => offer.currency === 'INR' ? `${sym}${Math.round(n).toLocaleString()}` : `${sym}${Number(n).toFixed(2)}`;
  const resumeHref = `/cart-recovery?tx=${encodeURIComponent(offer.session_id || '')}&code=${offer.code || 'COMEBACK50'}`;

  return (
    <div
      className="mb-6 bg-gradient-to-r from-[#FEF3C7] to-[#FDE68A] border border-[#FCD34D] rounded-lg p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 relative"
      data-testid={dataTestId}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="bg-[#A16207] text-white p-2 rounded-full flex-shrink-0">
          <Tag className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#A16207] font-bold mb-0.5">
            One-off return-customer offer
          </p>
          <p className="text-sm sm:text-[15px] text-[#78350F] font-semibold leading-tight">
            <span className="line-through text-[#A16207]/60 font-normal mr-1.5">{fmt(offer.original_amount)}</span>
            <span className="text-[#78350F]">{fmt(offer.discounted_amount)}</span>
            <span className="text-[#A16207] font-normal ml-2">— {offer.discount_pct}% off, applied automatically</span>
          </p>
          {offer.expires_at && (
            <p className="text-[11px] text-[#92400E] mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Expires in 24 hours
            </p>
          )}
        </div>
      </div>
      <Link
        to={resumeHref}
        className="bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs uppercase tracking-widest font-semibold px-5 py-2.5 rounded transition-colors w-full sm:w-auto text-center flex-shrink-0"
        data-testid={`${dataTestId}-resume`}
      >
        Finish my order
      </Link>
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 sm:static sm:ml-1 text-[#A16207] hover:text-[#78350F] p-1"
        title="Dismiss"
        data-testid={`${dataTestId}-dismiss`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default RecoveryDiscountBanner;
